export type HungerState = {
  hunger: number;
  maxHunger: number;
  saturation: number;
};

const HUNGER_DRAIN_RATE = 0.04;
const HUNGER_SPRINT_RATE = 0.25; // running makes you properly hungry

/** Below this the player is too tired to sprint (Minecraft-like). */
export const SPRINT_MIN_HUNGER = 6;
const STARVATION_DAMAGE_RATE = 1;

/** At/above this hunger the player naturally regenerates health. */
export const REGEN_MIN_HUNGER = 18;
const REGEN_RATE = 1; // HP per second while well-fed

export function createHungerState(): HungerState {
  return {
    hunger: 20,
    maxHunger: 20,
    saturation: 5,
  };
}

export function updateHunger(state: HungerState, dt: number, isSprinting: boolean): number {
  const rate = isSprinting ? HUNGER_SPRINT_RATE : HUNGER_DRAIN_RATE;

  if (state.saturation > 0) {
    state.saturation = Math.max(0, state.saturation - rate * dt);
  } else {
    state.hunger = Math.max(0, state.hunger - rate * dt);
  }

  if (state.hunger <= 0) {
    return STARVATION_DAMAGE_RATE * dt;
  }
  return 0;
}

export function addFood(state: HungerState, hunger: number, saturation: number) {
  state.hunger = Math.min(state.maxHunger, state.hunger + hunger);
  state.saturation = Math.min(state.maxHunger, state.saturation + saturation);
}

/**
 * Health regenerated this frame — the player heals while well-fed
 * (hunger ≥ 18) and below max HP. Regen slowly drains saturation.
 */
export function computeRegen(
  state: HungerState,
  dt: number,
  health: number,
  maxHealth: number
): number {
  if (health >= maxHealth) return 0;
  if (state.hunger < REGEN_MIN_HUNGER) return 0;
  // Healing costs a little saturation, so it's not infinite
  state.saturation = Math.max(0, state.saturation - dt * 0.5);
  return REGEN_RATE * dt;
}
